// src/components/SeoHead.tsx
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { seoForPath } from "@/lib/seo/routes";

/**
 * Emits <link rel="canonical"> and the hreflang cluster for the current route.
 *
 * These used to be injected by scripts/prerender.mjs alone. When prerendering
 * left the default build (vanilla Puppeteer cannot launch in Vercel's build
 * container) every canonical and hreflang tag on the site disappeared with it,
 * because nothing in the React tree had ever produced them. Emitting them here
 * makes the head correct without prerendering; prerendering then only adds a
 * static copy for crawlers that do not run JS.
 *
 * Also emits og:url = canonical. AppLayout declares og:url as the homepage so
 * that every page has one, and a page that did not override it (WhyFyrris was the
 * reported case; in fact all of them) shared the homepage URL when pasted into a
 * social post. SeoHead renders after AppLayout's Helmet, so this wins.
 *
 * Renders nothing for paths outside the public marketing surface — auth routes
 * and the noindexed booking forms get no canonical, which is what we want.
 */
export default function SeoHead() {
  const { pathname } = useLocation();
  const seo = seoForPath(pathname);

  if (!seo) return null;

  return (
    <Helmet>
      <link rel="canonical" href={seo.canonical} />
      <meta property="og:url" content={seo.canonical} />
      {seo.alternates.map((a) => (
        <link key={a.hreflang} rel="alternate" hrefLang={a.hreflang} href={a.href} />
      ))}
    </Helmet>
  );
}
